import { action } from '@ember/object';
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { IllustrationName } from '../types/components/prism/illustration.ts';

type ButtonType = 'confirm' | 'cancel';

export interface ButtonOptions {
  /**
   * Optionally, you can provide a custom class to be applied on the button.
   */
  className?: string;
  /**
   * The label text for the button, if not provided defaults provided in `setup()` call will apply.
   */
  text?: string;
}

type Buttons = {
  [key in ButtonType]: ButtonOptions;
};

type AlertOptions = {
  buttons?: Partial<Buttons>;
  dangerMode?: boolean;
  htmlContent?: Element;
  icon?: IllustrationName;
  text?: string;
  title?: string;
  // Provided for backwards compatibility, should not be used
  unsafe_buttonClickCanCloseAlert?: false;
  // Provided for backwards compatibility, should not be used
  unsafe_outsideClickCanCloseAlert?: false;
};

type PartialAlertOptions = Partial<AlertOptions>;

const ICONS = {
  success: '100x100_success',
  error: '100x100_critical',
  warning: '100x100_warning',
} as const;

const DEFAULT_BUTTONS = {
  confirm: {
    text: 'Ok',
  },
};

type SetTranslationsOptions = {
  showConfirmWarningAlert?: {
    buttons: {
      cancel: {
        text: string;
      };
      confirm: {
        text: string;
      };
    };
    title: string;
  };
  showErrorAlert: {
    text: string;
    title: string;
  };
};

export default class AlertingService extends Service {
  /**
   * `pendingPromise` becomes a pending promise as soon as an Alert is openened, and resolves as soon as the
   * user either cancels or confirms, or clicks outside of the alert to dismiss it.
   */
  @tracked activeAlert:
    | (AlertOptions & { pendingPromise?: (hasConfirmed: boolean) => void })
    | null = null;

  optionsForShowConfirmWarningAlert: AlertOptions = {};
  optionsForShowErrorAlert: AlertOptions = {};

  setTranslations({
    showErrorAlert,
    showConfirmWarningAlert,
  }: SetTranslationsOptions): void {
    this.optionsForShowErrorAlert = {
      title: showErrorAlert.title,
      text: showErrorAlert.text,
    };

    this.optionsForShowConfirmWarningAlert = {
      buttons: {
        cancel: {
          text: showConfirmWarningAlert?.buttons?.cancel.text,
        },
        confirm: {
          text: showConfirmWarningAlert?.buttons?.confirm.text,
        },
      },
      title: showConfirmWarningAlert?.title,
    };
  }

  async showConfirmWarningAlert(
    customOptions: PartialAlertOptions = {},
  ): Promise<boolean> {
    if (this.activeAlert?.pendingPromise) {
      this.activeAlert.pendingPromise(false);
    }

    this.activeAlert = {
      icon: ICONS.warning,
      buttons: DEFAULT_BUTTONS,
      ...this.optionsForShowConfirmWarningAlert,
      ...customOptions,
    };

    return new Promise((resolve) => {
      if (this.activeAlert) {
        this.activeAlert.pendingPromise = resolve;
      }
    });
  }

  async showErrorAlert(
    customOptions: PartialAlertOptions = {},
  ): Promise<boolean> {
    if (this.activeAlert?.pendingPromise) {
      this.activeAlert.pendingPromise(false);
    }

    this.activeAlert = {
      icon: ICONS.error,
      buttons: DEFAULT_BUTTONS,
      ...this.optionsForShowErrorAlert,
      ...customOptions,
    };

    return new Promise((resolve) => {
      if (this.activeAlert) {
        this.activeAlert.pendingPromise = resolve;
      }
    });
  }

  async showSuccessAlert(
    customOptions: PartialAlertOptions = {},
  ): Promise<boolean> {
    if (this.activeAlert?.pendingPromise) {
      this.activeAlert.pendingPromise(false);
    }

    this.activeAlert = {
      icon: ICONS.success,
      buttons: DEFAULT_BUTTONS,
      ...customOptions,
    };

    return new Promise((resolve) => {
      if (this.activeAlert) {
        this.activeAlert.pendingPromise = resolve;
      }
    });
  }

  async showWarningAlert(
    customOptions: PartialAlertOptions = {},
  ): Promise<boolean> {
    if (this.activeAlert?.pendingPromise) {
      this.activeAlert.pendingPromise(false);
    }

    this.activeAlert = {
      icon: ICONS.warning,
      buttons: DEFAULT_BUTTONS,
      ...customOptions,
    };

    return new Promise((resolve) => {
      if (this.activeAlert) {
        this.activeAlert.pendingPromise = resolve;
      }
    });
  }

  @action
  onClose({
    hasConfirmed,
    unsafe_canCloseAlert,
  }: {
    hasConfirmed: boolean;
    unsafe_canCloseAlert?: boolean;
  }): void {
    this.activeAlert?.pendingPromise?.(hasConfirmed);

    // Provided for backwards compatibility, should not be used
    if (unsafe_canCloseAlert) {
      this.activeAlert = null;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    alerting: AlertingService;
  }
}
